import React, { useState } from 'react';
import { Tabs } from './ui/Tabs';
import { Button } from './ui/Button';
import { ArrowLeft, Save } from 'lucide-react';
import { HeaderTab } from './KYCGroupTabs/HeaderTab';
import { PersonalInfoTab, KYCField } from './KYCGroupTabs/PersonalInfoTab';
import { BusinessInfoTab } from './KYCGroupTabs/BusinessInfoTab';
import { FinancialTab } from './KYCGroupTabs/FinancialTab';
import { DocumentsTab } from './KYCGroupTabs/DocumentsTab';
import { BankInfoTab } from './KYCGroupTabs/BankInfoTab';
import { VerificationsTab } from './KYCGroupTabs/VerificationsTab';
interface KYCGroupFormProps {
  onBack: () => void;
  onSave: () => void;
}
// Initial Data Generators
const createField = (id: string, label: string): KYCField => ({
  id,
  label,
  selected: false,
  mandatory: false,
  optional: false
});
export function KYCGroupForm({
  onBack,
  onSave
}: KYCGroupFormProps) {
  const [activeTab, setActiveTab] = useState('header');
  // Form State
  const [headerData, setHeaderData] = useState({
    groupId: '',
    groupName: '',
    status: 'active'
  });
  const [personalFields, setPersonalFields] = useState<KYCField[]>([createField('name', 'Name'), createField('cnic', 'CNIC'), createField('cnic_expiry', 'CNIC Expiry'), createField('gender', 'Gender'), createField('province', 'Province'), createField('city', 'City'), createField('address', 'Address'), createField('date_of_birth', 'Date of Birth')]);
  const [businessFields, setBusinessFields] = useState<KYCField[]>([createField('business_name', 'Business Name'), createField('business_type', 'Business Type'), createField('business_tenure', 'Business Tenure'), createField('business_address', 'Business Address'), createField('business_location', 'Business Location'), createField('business_ownership_type', 'Business Ownership Type'), createField('ntn', 'NTN')]);
  const [financialFields, setFinancialFields] = useState<KYCField[]>([createField('monthly_sales_volume', 'Monthly Sales Volume'), createField('transaction_volume_category', 'Transaction Volume Category'), createField('average_transaction_spend', 'Average Transaction Spend'), createField('pos_sales_share', 'POS Sales Share'), createField('cash_sales_share', 'Cash Sales Share'), createField('pos_transaction_frequency', 'POS Transaction Frequency')]);
  const [documentFields, setDocumentFields] = useState<KYCField[]>([createField('utility_bill', 'Utility Bill'), createField('shop_banner', 'Shop Banner / Premises Photo'), createField('bank_statement', 'Bank Statement'), createField('tax_registration', 'Tax Registration / NTN Document'), createField('cnic_upload', 'CNIC Upload'), createField('live_photograph', 'Live Photograph'), createField('risk_profiling', 'Risk Profiling Questionnaire')]);
  const [bankFields, setBankFields] = useState<KYCField[]>([createField('bank_type', 'Bank Type'), createField('bank_name', 'Bank Name'), createField('account_number', 'Account Number')]);
  const [verificationFields, setVerificationFields] = useState<KYCField[]>([createField('local_aml', 'Local AML List Check'), createField('global_aml', 'AML Global Check (Idenfo or equivalent)'), createField('nadra_sahulat', 'NADRA e-Sahulat CNIC verification'), createField('nadra_verisys', 'NADRA Verisys facial match verification'), createField('fbr_tax', 'FBR Taxpayer Verification'), createField('ecib', 'eCIB Credit Bureau Inquiry'), createField('sms_otp', 'SMS OTP Verification'), createField('email_otp', 'Email OTP Verification')]);
  // Handlers
  const handleHeaderChange = (field: string, value: string) => {
    setHeaderData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const updateFields = (fields: KYCField[], setFields: React.Dispatch<React.SetStateAction<KYCField[]>>, id: string, updates: Partial<KYCField>) => {
    setFields(fields.map(f => f.id === id ? {
      ...f,
      ...updates
    } : f));
  };
  const toggleAll = (fields: KYCField[], setFields: React.Dispatch<React.SetStateAction<KYCField[]>>, selected: boolean) => {
    setFields(fields.map(f => ({
      ...f,
      selected
    })));
  };
  const tabs = [{
    id: 'header',
    label: '1. Group Header'
  }, {
    id: 'personal',
    label: '2. Personal Info'
  }, {
    id: 'business',
    label: '3. Business Info'
  }, {
    id: 'financial',
    label: '4. Financial & Sales'
  }, {
    id: 'documents',
    label: '5. Documents'
  }, {
    id: 'bank',
    label: '6. Bank Info'
  }, {
    id: 'verifications',
    label: '7. Verifications'
  }];
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-330">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-331">
        <div className="flex items-center" data-id="element-332">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-333">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-334" />
            Back to List
          </Button>
          <div data-id="element-335">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-336">
              New KYC Group Configuration
            </h1>
            <p className="text-sm text-gray-500" data-id="element-337">
              Configure KYC requirements for this group
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-338">
          <Button variant="secondary" onClick={onBack} data-id="element-339">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-340">
            <Save className="h-4 w-4 mr-2" data-id="element-341" />
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-[73px] z-10 bg-white shadow-sm" data-id="element-342">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-343" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full" data-id="element-344">
        {activeTab === 'header' && <HeaderTab data={headerData} onChange={handleHeaderChange} data-id="element-345" />}
        {activeTab === 'personal' && <PersonalInfoTab fields={personalFields} onFieldChange={(id, updates) => updateFields(personalFields, setPersonalFields, id, updates)} onSelectAll={selected => toggleAll(personalFields, setPersonalFields, selected)} data-id="element-346" />}
        {activeTab === 'business' && <BusinessInfoTab fields={businessFields} onFieldChange={(id, updates) => updateFields(businessFields, setBusinessFields, id, updates)} onSelectAll={selected => toggleAll(businessFields, setBusinessFields, selected)} data-id="element-347" />}
        {activeTab === 'financial' && <FinancialTab fields={financialFields} onFieldChange={(id, updates) => updateFields(financialFields, setFinancialFields, id, updates)} onSelectAll={selected => toggleAll(financialFields, setFinancialFields, selected)} data-id="element-348" />}
        {activeTab === 'documents' && <DocumentsTab fields={documentFields} onFieldChange={(id, updates) => updateFields(documentFields, setDocumentFields, id, updates)} onSelectAll={selected => toggleAll(documentFields, setDocumentFields, selected)} data-id="element-349" />}
        {activeTab === 'bank' && <BankInfoTab fields={bankFields} onFieldChange={(id, updates) => updateFields(bankFields, setBankFields, id, updates)} onSelectAll={selected => toggleAll(bankFields, setBankFields, selected)} data-id="element-350" />}
        {activeTab === 'verifications' && <VerificationsTab fields={verificationFields} onFieldChange={(id, updates) => updateFields(verificationFields, setVerificationFields, id, updates)} onSelectAll={selected => toggleAll(verificationFields, setVerificationFields, selected)} data-id="element-351" />}
      </div>
    </div>;
}